import { Divider, Step, StepLabel, StepLabelProps, Stepper, StepProps } from '@mui/material'
import { AppBox, AppButton, AppIcon, AppTypography } from 'components'
import { FC, useState } from 'react'
import { CancelMembershipConfirmation } from './confirmation/CancelMembershipConfirmation'
import { PauseMembershipConfimation } from './confirmation/PauseMembershipConfirmation'
import { CancelMembershipSelect } from './select/CancelMembershipSelect'
import { CancelMembershipVerification } from './verify/CancelMembershipVerification'
import { PauseMembershipVerification } from './verify/PauseMembershipVerification'
import {
  CancelMembershipPayload,
  ChangeMembershipResponse,
  MembershipApi,
  PauseMembershipPayload,
  SessionModel,
} from 'sdk'
import { useMutation } from '@tanstack/react-query'
import { ApiQueryKeys, RoleTypes } from 'globals/constants'
import { useSnackbar } from 'notistack'
import { useAppDispatch } from 'store/app.store'
import { AuthSliceSelector, setMemberships, setRoles, setSession, setTrialExpiry } from 'store/slices'
import { useSelector } from 'react-redux'

export enum CancelMembershipType {
  Cancel = 'Cancel',
  Pause = 'Pause',
}
interface Props {
  closeDialog: () => void
}

export const CancelMembership: FC<Props> = ({ closeDialog }) => {
  const membershipApi = new MembershipApi()
  const steps = ['Select', 'Verify', 'Confirmation']
  const [activeStep, setActiveStep] = useState(0)
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()
  const { CurrentUser, Roles } = useSelector(AuthSliceSelector)
  const [selectedType, setSelectedType] = useState<CancelMembershipType>()
  const [isVerificationFilled, setIsVerificationFilled] = useState<boolean>()
  const [pauseMembershipPayload, setPausedMembershipPayload] = useState<PauseMembershipPayload>()
  const [cancelMembershipPayload, setCancelMembershipPayload] = useState<CancelMembershipPayload>({
    UserId: CurrentUser?.UserId!,
  })

  const { mutate: cancelMembership, isPending: isCanceling } = useMutation({
    mutationKey: [ApiQueryKeys.memberships.CancelMembership],
    mutationFn: () => membershipApi.CancelMembership(cancelMembershipPayload!),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: (data: ChangeMembershipResponse) => {
      if (data) {
        dispatch(setMemberships(data.Memberships))
        const copy: SessionModel = JSON.parse(JSON.stringify(CurrentUser))
        copy.Status = data.UserStatus
        if (Roles.includes(RoleTypes.TrialUser)) {
          const roleCopy = JSON.parse(JSON.stringify(Roles)) as RoleTypes[]
          dispatch(setRoles(roleCopy.filter((x) => x !== RoleTypes.TrialUser)))
        }
        dispatch(setSession(copy))
        dispatch(setTrialExpiry(null))
        enqueueSnackbar('Membership will be cancelled on next billing cycle', { variant: 'success' })
        closeDialog()
      }
    },
  })
  const { mutate: pauseMembership, isPending: isPausing } = useMutation({
    mutationKey: [ApiQueryKeys.memberships.PauseMembership],
    mutationFn: () => membershipApi.PauseMembership(pauseMembershipPayload!),
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
    onSuccess: (data: ChangeMembershipResponse) => {
      if (data) {
        dispatch(setMemberships(data.Memberships))
        const copy: SessionModel = JSON.parse(JSON.stringify(CurrentUser))
        copy.Status = data.UserStatus
        if (Roles.includes(RoleTypes.TrialUser)) {
          const roleCopy = JSON.parse(JSON.stringify(Roles)) as RoleTypes[]
          dispatch(setRoles(roleCopy.filter((x) => x !== RoleTypes.TrialUser)))
        }
        dispatch(setSession(copy))
        dispatch(setTrialExpiry(null))
        enqueueSnackbar('Membership will be paused on next billing cycle', { variant: 'success' })
        closeDialog()
      }
    },
  })

  const handleSelectType = (type: CancelMembershipType) => {
    setSelectedType(type)
    handleContinueClick()
  }
  const renderStepsContent = () => {
    switch (activeStep) {
      case 0:
        return <CancelMembershipSelect handleCancelMembershipTyp={handleSelectType} />
      case 1:
        return (
          <>
            {selectedType === CancelMembershipType.Pause ? (
              <PauseMembershipVerification
                isFormFilled={setIsVerificationFilled}
                onLengthChange={(val: string) => setPausedMembershipPayload((prev) => ({ ...prev, MonthsDelay: val }))}
              />
            ) : (
              <CancelMembershipVerification
                isFormFilled={setIsVerificationFilled}
                onReasonChange={(val: string) => setCancelMembershipPayload((prev) => ({ ...prev, Reason: val }))}
                onCommentsChange={(val: string) => setCancelMembershipPayload((prev) => ({ ...prev!, Comment: val }))}
              />
            )}
          </>
        )
      case 2:
        return (
          <>
            {selectedType === CancelMembershipType.Pause ? (
              <PauseMembershipConfimation />
            ) : (
              <CancelMembershipConfirmation />
            )}
          </>
        )
      default:
        return null
    }
  }

  const nextButtonLabel = () => {
    switch (activeStep) {
      case 1:
        return 'Continue'
      case 2:
        return 'Close'
    }
  }

  const handleContinueClick = () => {
    if (activeStep === 1) {
      if (selectedType === CancelMembershipType.Pause) {
        pauseMembership()
      } else {
        cancelMembership()
      }
      return
    } else if (activeStep == 2) {
      closeDialog()
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }
  const handleBackClick = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }
  return (
    <>
      <AppBox>
        <AppBox paddingBottom={0}>
          <Stepper
            activeStep={activeStep}
            connector={<Divider sx={{ width: 40, marginX: 0.5, borderWidth: 1, borderColor: 'primary.600' }} />}
            sx={{ justifyContent: 'center' }}
          >
            {steps.map((step, index) => {
              const stepProps: StepProps = {}
              const labelProps: StepLabelProps = {}
              return (
                <Step key={step} {...stepProps}>
                  <StepLabel
                    icon={
                      <AppBox
                        width={25}
                        height={25}
                        backgroundColor="primary.100"
                        borderRadius={12}
                        textAlign={'center'}
                        justifySelf={'center'}
                        paddingTop={0.2}
                      >
                        {activeStep > index ? (
                          <AppIcon icon={'material-symbols:check'} size="medium" color="primary.600" />
                        ) : (
                          <AppTypography fontSize={14}>{index + 1}</AppTypography>
                        )}
                      </AppBox>
                    }
                    {...labelProps}
                  >
                    <AppTypography fontSize={16} color={'primary.600'} fontWeight={600}>
                      {step}
                    </AppTypography>
                  </StepLabel>
                </Step>
              )
            })}
          </Stepper>
        </AppBox>
        <AppBox paddingY={2}>{renderStepsContent()}</AppBox>
        <AppBox If={activeStep > 0}>
          <AppBox display={'flex'} gap={2} justifyContent={'flex-end'}>
            {activeStep !== steps.length - 1 && (
              <AppButton
                variant="outlined"
                onClick={() => handleBackClick()}
                size="large"
                color="primary"
                sidePaddings={6}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </AppButton>
            )}
            <AppButton
              sidePaddings={6}
              variant="contained"
              size="large"
              isLoading={isCanceling || isPausing}
              disabled={!isVerificationFilled}
              onClick={() => handleContinueClick()}
            >
              {nextButtonLabel()}
            </AppButton>
          </AppBox>
        </AppBox>
      </AppBox>
    </>
  )
}
